import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Users, DollarSign, FileText, Send, Award, Bell, Check, X } from "lucide-react";
import PageLayout from "../../components/layout/PageLayout";
import { useAuth } from "../../lib/auth";
import api, { formatApiError } from "../../lib/api";

const TABS = ["Overview", "Applications", "Athletes", "Deals", "Sponsors", "Opportunities", "Inquiries"];

export default function MgmtDashboard() {
    const { user } = useAuth();
    const [tab, setTab] = useState("Overview");
    const [summary, setSummary] = useState(null);
    const [apps, setApps] = useState([]);
    const [athletes, setAthletes] = useState([]);
    const [deals, setDeals] = useState([]);
    const [proposals, setProposals] = useState([]);
    const [opps, setOpps] = useState([]);
    const [inquiries, setInquiries] = useState([]);

    const load = useCallback(() => {
        api.get("/mgmt/summary").then(r => setSummary(r.data)).catch(() => {});
        api.get("/applications").then(r => setApps(r.data)).catch(() => {});
        api.get("/athletes").then(r => setAthletes(r.data)).catch(() => {});
        api.get("/deals").then(r => setDeals(r.data)).catch(() => {});
        api.get("/proposals").then(r => setProposals(r.data)).catch(() => {});
        api.get("/opportunities").then(r => setOpps(r.data)).catch(() => {});
        api.get("/inquiries").then(r => setInquiries(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const updateAppStatus = async (id, status) => {
        try { await api.patch(`/applications/${id}`, { status }); toast.success(`Application ${status}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const updateProposalStatus = async (id, status) => {
        try { await api.patch(`/proposals/${id}`, { status }); toast.success(`Proposal ${status}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const dealChart = deals.slice(0, 8).map(d => ({ name: d.brand.slice(0, 10), value: d.compensation }));

    return (
        <PageLayout>
            <section className="pt-28 pb-8 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-3">Management · Executive Dashboard</div>
                    <div className="flex flex-wrap justify-between items-end gap-4">
                        <div>
                            <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight" data-testid="mgmt-heading">
                                THE <span className="text-[#d4af37]">COMMAND CENTER</span>
                            </h1>
                            <div className="text-white/50 text-sm mt-2">{user.name} · {user.role}</div>
                        </div>
                        <Link to="/mgmt/football" className="btn-outline" data-testid="mgmt-football-cta">
                            Football Control Room →
                        </Link>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8 flex gap-1 overflow-x-auto">
                    {TABS.map(t => (
                        <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
                            className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50 hover:text-white"}`}>{t}</button>
                    ))}
                </div>
            </section>

            <section className="py-10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    {tab === "Overview" && summary && (
                        <div className="space-y-6" data-testid="tab-overview-content">
                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {[
                                    { key: "total-athletes", icon: Users, l: "Total Athletes", v: summary.total_athletes },
                                    { key: "new-applications", icon: Bell, l: "New Applications", v: summary.new_applications },
                                    { key: "active-deals", icon: Award, l: "Active Deals", v: summary.active_deals },
                                    { key: "open-opps", icon: Send, l: "Open Opportunities", v: summary.open_opportunities },
                                    { key: "gross-value", icon: DollarSign, l: "Gross Deal Value", v: `$${summary.gross_deal_value.toLocaleString()}` },
                                    { key: "agency-rev", icon: DollarSign, l: "Agency Revenue", v: `$${summary.agency_revenue.toLocaleString()}` },
                                    { key: "athlete-pay", icon: DollarSign, l: "Athlete Payments", v: `$${summary.athlete_payments.toLocaleString()}` },
                                    { key: "pending-proposals", icon: FileText, l: "Pending Proposals", v: summary.pending_proposals },
                                ].map(({key, icon: I, l, v}) => (
                                    <div key={key} className="surface p-6" data-testid={`kpi-${key}`}>
                                        <I className="text-[#d4af37]" size={20} strokeWidth={1.5} />
                                        <div className="font-display text-3xl text-white mt-4 leading-none">{v}</div>
                                        <div className="text-white/40 text-xs uppercase tracking-widest mt-3">{l}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid lg:grid-cols-3 gap-4">
                                <div className="surface p-6 lg:col-span-2">
                                    <div className="section-label mb-4">Deal Value by Brand</div>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={dealChart}>
                                            <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                            <XAxis dataKey="name" stroke="#555" tick={{ fill: "#888", fontSize: 11 }} />
                                            <YAxis stroke="#555" tick={{ fill: "#888", fontSize: 11 }} />
                                            <Tooltip contentStyle={{ background: "#0f0f11", border: "1px solid #333", color: "#fff" }} />
                                            <Bar dataKey="value" fill="#d4af37" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="surface p-6">
                                    <div className="section-label mb-4">Attention Required</div>
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center border-b border-white/10 pb-3">
                                            <div className="text-white/70 text-sm">Applications to review</div>
                                            <div className="font-display text-2xl text-[#d4af37]">{summary.new_applications}</div>
                                        </div>
                                        <div className="flex justify-between items-center border-b border-white/10 pb-3">
                                            <div className="text-white/70 text-sm">Proposals to review</div>
                                            <div className="font-display text-2xl text-[#d4af37]">{summary.pending_proposals}</div>
                                        </div>
                                        <div className="flex justify-between items-center border-b border-white/10 pb-3">
                                            <div className="text-white/70 text-sm">Content approvals</div>
                                            <div className="font-display text-2xl text-[#d4af37]">{summary.content_awaiting_approval}</div>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <div className="text-white/70 text-sm">New inquiries</div>
                                            <div className="font-display text-2xl text-[#d4af37]">{summary.new_inquiries}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === "Applications" && (
                        <div className="surface overflow-x-auto" data-testid="tab-applications-content">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Sport / School</th>
                                        <th className="text-left p-4">Submitted</th><th className="text-left p-4">Status</th><th className="p-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {apps.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-white/40">No applications yet.</td></tr> : apps.map(a => (
                                        <tr key={a.id} className="border-t border-white/10">
                                            <td className="p-4 text-white">{a.personal?.full_name || a.personal?.preferred_name || "—"}</td>
                                            <td className="p-4 text-white/70">{a.personal?.email || "—"}</td>
                                            <td className="p-4 text-white/70">{a.athletic?.sport} · {a.athletic?.school}</td>
                                            <td className="p-4 text-white/40 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                                            <td className="p-4"><span className="chip">{a.status}</span></td>
                                            <td className="p-4 text-right">
                                                <div className="flex gap-1 justify-end">
                                                    <button onClick={() => updateAppStatus(a.id, "approved")} className="chip hover:!bg-[#d4af37] hover:!text-black" title="Approve" data-testid={`approve-app-${a.id}`}><Check size={12}/></button>
                                                    <button onClick={() => updateAppStatus(a.id, "rejected")} className="chip" title="Reject" data-testid={`reject-app-${a.id}`}><X size={12}/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tab === "Athletes" && (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tab-athletes-content">
                            {athletes.map(a => (
                                <div key={a.id} className="surface surface-hover overflow-hidden">
                                    <div className="relative aspect-[4/3]">
                                        <img src={a.hero_image} alt={a.name} className="absolute inset-0 w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                                        <div className="absolute bottom-3 left-4 right-4">
                                            <div className="font-display text-lg text-white">{a.name}</div>
                                            <div className="text-white/70 text-xs uppercase tracking-widest">{a.sport} · {a.school}</div>
                                        </div>
                                    </div>
                                    <div className="p-4 flex justify-between items-center">
                                        <div className="text-[#d4af37] font-mono text-sm">{(a.social?.followers || 0).toLocaleString()}</div>
                                        <span className="chip">{a.visibility}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "Deals" && (
                        <div className="surface overflow-x-auto" data-testid="tab-deals-content">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Athlete</th><th className="text-left p-4">Brand</th><th className="text-left p-4">Campaign</th>
                                        <th className="text-right p-4">Value</th><th className="text-right p-4">Agency</th><th className="text-right p-4">Athlete Net</th>
                                        <th className="text-left p-4">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deals.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-white/40">No deals yet.</td></tr> : deals.map(d => (
                                        <tr key={d.id} className="border-t border-white/10">
                                            <td className="p-4 text-white/70">{d.athlete_id.slice(0,8)}</td>
                                            <td className="p-4 text-white">{d.brand}</td>
                                            <td className="p-4 text-white/70">{d.campaign}</td>
                                            <td className="p-4 text-right text-white">${d.compensation.toLocaleString()}</td>
                                            <td className="p-4 text-right text-[#d4af37]">${d.agency_share.toLocaleString()}</td>
                                            <td className="p-4 text-right text-white">${d.athlete_net.toLocaleString()}</td>
                                            <td className="p-4"><span className="chip">{d.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tab === "Sponsors" && (
                        <div className="surface overflow-x-auto" data-testid="tab-sponsors-content">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Campaign</th><th className="text-left p-4">Sponsor</th>
                                        <th className="text-right p-4">Budget</th><th className="text-left p-4">Status</th><th className="p-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {proposals.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-white/40">No proposals yet.</td></tr> : proposals.map(p => (
                                        <tr key={p.id} className="border-t border-white/10">
                                            <td className="p-4 text-white">{p.campaign_title}</td>
                                            <td className="p-4 text-white/70">{p.sponsor_name}</td>
                                            <td className="p-4 text-right text-[#d4af37]">${p.proposed_budget.toLocaleString()}</td>
                                            <td className="p-4"><span className="chip">{p.status}</span></td>
                                            <td className="p-4 text-right">
                                                <div className="flex gap-1 justify-end">
                                                    <button onClick={() => updateProposalStatus(p.id, "accepted")} className="chip" data-testid={`accept-prop-${p.id}`}><Check size={12}/></button>
                                                    <button onClick={() => updateProposalStatus(p.id, "declined")} className="chip" data-testid={`decline-prop-${p.id}`}><X size={12}/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tab === "Opportunities" && (
                        <div className="grid md:grid-cols-2 gap-4" data-testid="tab-opportunities-content">
                            {opps.map(o => (
                                <div key={o.id} className="surface p-6">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-display text-2xl text-white">{o.title}</div>
                                            <div className="text-[#d4af37] text-xs uppercase tracking-widest mt-1">{o.brand}</div>
                                        </div>
                                        <span className="chip">{o.status}</span>
                                    </div>
                                    <p className="text-white/60 text-sm mt-4">{o.description}</p>
                                    <div className="mt-5 flex justify-between border-t border-white/10 pt-4">
                                        <div>
                                            <div className="text-white/40 text-[10px] uppercase tracking-widest">Compensation</div>
                                            <div className="font-display text-2xl text-[#d4af37]">${o.compensation.toLocaleString()}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-white/40 text-[10px] uppercase tracking-widest">Deadline</div>
                                            <div className="text-white text-sm">{o.deadline || "—"}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "Inquiries" && (
                        <div className="space-y-3" data-testid="tab-inquiries-content">
                            {inquiries.length === 0 ? <div className="text-center py-16 text-white/40">No inquiries yet.</div> : inquiries.map(q => (
                                <div key={q.id} className="surface p-6">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-display text-lg text-white">{q.subject || q.inquiry_type}</div>
                                            <div className="text-white/50 text-xs mt-1">{q.name} · {q.email} · {q.phone}</div>
                                        </div>
                                        <span className="chip">{q.inquiry_type}</span>
                                    </div>
                                    <p className="text-white/70 text-sm mt-4">{q.message}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </PageLayout>
    );
}
